// 📊 SERVIÇO DE COLETA DE DADOS DO PACIENTE
// Sistema: HEPATO TX RONDÔNIA
// Arquivo: src/modules/ia-analise/services/coletarDadosPaciente.ts

import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../../lib/firebase";
import { Paciente } from "../../../types";
import { DadosAnonimizados } from "../types";
import {
  anonimizarDadosPaciente,
  validarAnonimizacao,
} from "./anonimizarDados";
// B351 passo 5b (AI_153) — modalidade do doador e conjunto mínimo do receptor.
import { ehDoadorFigado } from "../../../shared/utils/modalidadeAtendimento";
import {
  montarConjuntoMinimoReceptor,
  ConjuntoMinimoReceptor,
} from "../../../shared/utils/conjuntoMinimoReceptor";

/**
 * 📦 COLETAR TODOS OS DADOS DO PACIENTE
 *
 * Busca informações de todas as coleções relevantes:
 * - Consultas/Evolução Clínica
 * - Exames Laboratoriais
 * - Marcadores Tumorais
 * - Sorologias
 * - Exames de Imagem
 *
 * Retorna dados já anonimizados para envio à API
 */
export async function coletarDadosCompletos(
  paciente: Paciente
): Promise<DadosAnonimizados> {
  console.log(`📊 Coletando dados do paciente ${paciente.id}...`);

  try {
    // 🩺 1. Buscar Consultas/Evolução
    const consultas = await buscarConsultas(paciente.id);
    console.log(`✅ ${consultas.length} consultas encontradas`);

    // 🔬 2. Buscar Exames Laboratoriais
    const examesLab = await buscarExamesLaboratoriais(paciente.id);
    console.log(`✅ ${examesLab.length} exames laboratoriais encontrados`);
    // [B196] Dump dos 2 primeiros exames removido (LGPD) — contagem acima basta.

    // 🎯 3. Buscar Marcadores Tumorais
    const marcadores = await buscarMarcadores(paciente.id);
    console.log(`✅ ${marcadores.length} marcadores encontrados`);

    // 🦠 4. Buscar Sorologias
    const sorologias = await buscarSorologias(paciente.id);
    console.log(`✅ ${sorologias.length} sorologias encontradas`);

    // 📷 5. Buscar Exames de Imagem
    const imagens = await buscarExamesImagem(paciente.id);
    console.log(`✅ ${imagens.length} exames de imagem encontrados`);

    // 💊 6. Buscar Prescrições
    const prescricoes = await buscarPrescricoes(paciente.id);
    console.log(`✅ ${prescricoes.length} prescrições encontradas`);

    // 📋 7. Extrair Avaliação Clínica e Dados do Transplante do próprio paciente
    // ═══════════════════════════════════════════════════════════════════
    // [AI_55 Patch 2.2.2] Detecção unificada de Tx — cobre 3 caminhos:
    //   1. transplantes[]      — Multi-TX (aba Dados do Tx)
    //   2. dadosTransplante    — singular legado
    //   3. inscricaoTx         — aba "Inscrição p/ Tx" (rotulada na UI
    //                            como "Avaliação Clínica" — caminho mais
    //                            usado historicamente)
    // Helper extrairDataTransplante() trata todos os 3 + parser BR/ISO
    // sem viés de timezone. Logs de diagnóstico removidos após validação.
    // ═══════════════════════════════════════════════════════════════════
    const avaliacaoClinica = paciente.avaliacaoClinica || null;
    const inscricaoTx = paciente.inscricaoTx || null;

    // [AI_55 Patch 2.2.2] Detecção unificada de Tx — cobre 3 caminhos
    const dataTxUnificada = extrairDataTransplante(paciente);
    let fonteTx:
      | "transplantes[]"
      | "dadosTransplante"
      | "inscricaoTx"
      | "nenhum" = "nenhum";
    let dadosTransplante: any[] = [];

    if (
      Array.isArray(paciente.transplantes) &&
      paciente.transplantes.length > 0
    ) {
      dadosTransplante = paciente.transplantes;
      fonteTx = "transplantes[]";
    } else if (paciente.dadosTransplante) {
      dadosTransplante = [paciente.dadosTransplante];
      fonteTx = "dadosTransplante";
    } else if (paciente.inscricaoTx?.dataTransplante) {
      // Sintetiza objeto de Tx mínimo a partir do inscricaoTx
      // para alimentar a janela temporal de filtragem em laudos.
      // Datas convertidas para ISO para parsing consistente.
      dadosTransplante = [
        {
          dataTransplante: dataTxUnificada,
          centroTransplantador:
            paciente.inscricaoTx.hospitalTransplante || null,
          hospitalTransplante: paciente.inscricaoTx.hospitalTransplante || null,
          tipoSE: paciente.inscricaoTx.tipoSE || null,
          _origem: "inscricaoTx",
        },
      ];
      fonteTx = "inscricaoTx";
    }

    // 🔗 7b. DOADOR VIVO — conjunto mínimo do RECEPTOR (B351 passo 5b, AI_153)
    // ═══════════════════════════════════════════════════════════════════
    // Cruza DOIS prontuários, então é o ponto mais sensível desta modalidade:
    //   - só roda para `doador_figado` e só com receptor VINCULADO na aba;
    //   - não abre acesso novo (as rules já liberam leitura a usuário ativo, e o
    //     ExamesContext já baixa a coleção) — o dado NOVO nasce quando o TEXTO
    //     da análise incorpora atributos do receptor, e é por isso que a saída
    //     de `montarConjuntoMinimoReceptor` é FECHADA e sem identificadores;
    //   - falha aqui NUNCA derruba a análise: sem o conjunto, o prompt diz
    //     "receptor não vinculado" e o GRWR fica indeterminado (nunca inferido).
    // ═══════════════════════════════════════════════════════════════════
    let receptorMinimo: ConjuntoMinimoReceptor | null = null;
    if (ehDoadorFigado(paciente.tipoAtendimento)) {
      const aba: any = (paciente as any).avaliacaoDoador || {};
      const receptorId = String(aba.receptorId || "").trim();
      if (receptorId) {
        try {
          const snap = await getDoc(doc(db, "pacientes", receptorId));
          if (snap.exists()) {
            const receptor = snap.data() as any;
            const examesReceptor = await buscarExamesLaboratoriais(receptorId);
            receptorMinimo = montarConjuntoMinimoReceptor(
              receptor,
              examesReceptor,
              aba.tipoSanguineo,
              aba.pesoReceptor
            );
          } else {
            console.warn(
              "⚠️ [B351] receptor vinculado não encontrado — análise segue sem o conjunto mínimo"
            );
          }
        } catch (err) {
          // LGPD-safe: nunca logar dado do receptor, só a falha.
          console.warn(
            "⚠️ [B351] falha ao montar o conjunto mínimo do receptor (não-bloqueante)"
          );
        }
      }
      console.log(
        `✅ [B351] Doador: conjunto mínimo do receptor ${
          receptorMinimo ? "montado" : "ausente (não vinculado)"
        }`
      );
    }

    console.log(`✅ Avaliação clínica: ${avaliacaoClinica ? "SIM" : "NÃO"}`);
    const dataTxFormatada = dataTxUnificada
      ? (() => {
          const m = dataTxUnificada.match(/^(\d{4})-(\d{2})-(\d{2})/);
          return m ? `${m[3]}/${m[2]}/${m[1]}` : dataTxUnificada;
        })()
      : "nenhuma";
    console.log(
      `✅ Transplantes: ${dadosTransplante.length} (fonte: ${fonteTx}, dataTx: ${dataTxFormatada})`
    );

    // 🔒 8. Anonimizar todos os dados
    const dadosAnonimizados = await anonimizarDadosPaciente(
      paciente,
      consultas,
      examesLab,
      marcadores,
      sorologias,
      imagens,
      prescricoes,
      avaliacaoClinica,
      dadosTransplante,
      inscricaoTx,
      receptorMinimo
    );

    console.log(`✅ Dados coletados e anonimizados com sucesso!`);

    // 🔒 B209 — defesa em profundidade: confere que nenhum identificador do
    // paciente vazou para a saída. Não bloqueia (decisão de Leonardo, AI_93):
    // a barreira primária é a remoção na origem; isto só ALERTA uma regressão.
    // Log LGPD-safe: só o NOME do campo vazado, nunca o valor.
    const checagem = validarAnonimizacao(dadosAnonimizados, paciente);
    if (!checagem.ok) {
      console.warn(
        `⚠️ [B209] Possível vazamento de identificador na anonimização — campos: ${checagem.camposVazados.join(
          ", "
        )}. Análise prossegue (não bloqueante); investigar.`
      );
    }

    return dadosAnonimizados;
  } catch (error) {
    console.error("❌ Erro ao coletar dados do paciente:", error);
    throw new Error("Falha ao coletar dados para análise");
  }
}

/**
 * 🩺 BUSCAR CONSULTAS/EVOLUÇÃO CLÍNICA
 */
// ═══════════════════════════════════════════════════════════════════
// [AI_55 Patch 2.2.2] Helper unificado para extrair data de Tx
// ═══════════════════════════════════════════════════════════════════
// Existem 3 caminhos de cadastro de transplante no hepato.app:
//   1. transplantes[]      — array Multi-TX moderno (aba Dados do Tx)
//   2. dadosTransplante    — formato singular legado
//   3. inscricaoTx         — aba "Inscrição para Transplante"
//      (rotulada na UI como "Avaliação Clínica", nome legado mantido
//       por compatibilidade) — caminho mais usado historicamente.
//      Contém dataTransplante (formato BR DD/MM/AAAA) e
//      hospitalTransplante.
// Esta função tenta os 3 em ordem de preferência (mais novo → legado).
// Retorna a data como ISO string (AAAA-MM-DD) ou null.
// ═══════════════════════════════════════════════════════════════════
function parseDataParaISO(raw: any): string | null {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  // Formato BR: DD/MM/AAAA
  const matchBR = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (matchBR) {
    const [, dia, mes, ano] = matchBR;
    return `${ano}-${mes.padStart(2, "0")}-${dia.padStart(2, "0")}`;
  }
  // Formato ISO: AAAA-MM-DD (já correto)
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.substring(0, 10);
  return null;
}

function extrairDataTransplante(paciente: any): string | null {
  // 1. transplantes[] — formato Multi-TX (mais recente cronologicamente)
  if (
    Array.isArray(paciente?.transplantes) &&
    paciente.transplantes.length > 0
  ) {
    const ordenados = [...paciente.transplantes].sort((a, b) => {
      const dA = parseISOLocalTime(parseDataParaISO(a?.dataTransplante)) || 0;
      const dB = parseISOLocalTime(parseDataParaISO(b?.dataTransplante)) || 0;
      return dB - dA;
    });
    const dataMaisRecente = parseDataParaISO(ordenados[0]?.dataTransplante);
    if (dataMaisRecente) return dataMaisRecente;
  }
  // 2. dadosTransplante — singular legado
  const dataLegado = parseDataParaISO(
    paciente?.dadosTransplante?.dataTransplante
  );
  if (dataLegado) return dataLegado;
  // 3. inscricaoTx — caminho mais usado (aba "Avaliação Clínica" na UI)
  const dataInscricao = parseDataParaISO(
    paciente?.inscricaoTx?.dataTransplante
  );
  if (dataInscricao) return dataInscricao;
  return null;
}

// [AI_55 Patch 2.2.3] Parser de data ISO sem viés de timezone
// new Date("2025-12-29") interpreta como UTC meia-noite → em UTC-4
// volta a 28/12/2025 23:00 local. Usar Date(ano, mes-1, dia) força
// interpretação como meia-noite LOCAL, eliminando o off-by-one.
// Documentado nas specs (seção 4.4 — Datas e Fuso Horário).
function parseISOLocalTime(iso: string | null): number | null {
  if (!iso) return null;
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [, ano, mes, dia] = match;
  return new Date(
    parseInt(ano, 10),
    parseInt(mes, 10) - 1,
    parseInt(dia, 10)
  ).getTime();
}

async function buscarConsultas(pacienteId: string): Promise<any[]> {
  try {
    const consultasRef = collection(db, "evolucoes");
    const q = query(
      consultasRef,
      where("pacienteId", "==", pacienteId),
      orderBy("data", "desc")
    );

    const snapshot = await getDocs(q);
    // B323 Bloco 3 (AI_146): consulta cancelada NÃO alimenta a IA (o registro
    // foi anulado; a IA não deve raciocinar sobre ele).
    return snapshot.docs
      .map((doc) => ({ id: doc.id, ...(doc.data() as any) }))
      .filter((c: any) => c.cancelado !== true);
  } catch (error) {
    console.warn("⚠️ Erro ao buscar consultas:", error);
    return [];
  }
}

/**
 * 🔬 BUSCAR EXAMES LABORATORIAIS
 */
async function buscarExamesLaboratoriais(pacienteId: string): Promise<any[]> {
  try {
    const examesRef = collection(db, "exames");
    const q = query(
      examesRef,
      where("pacienteId", "==", pacienteId),
      orderBy("data", "desc")
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.warn("⚠️ Erro ao buscar exames laboratoriais:", error);
    return [];
  }
}

/**
 * 🎯 BUSCAR MARCADORES TUMORAIS
 */
async function buscarMarcadores(pacienteId: string): Promise<any[]> {
  try {
    const marcadoresRef = collection(db, "marcadores");
    const q = query(
      marcadoresRef,
      where("pacienteId", "==", pacienteId),
      orderBy("dataColeta", "desc")
    );

    const snapshot = await getDocs(q);
    const marcadores = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // [B196] Dump da estrutura dos marcadores removido (LGPD) — só contagem.
    console.log(`🔍 Marcadores coletados: ${marcadores.length}`);

    return marcadores;
  } catch (error) {
    console.warn("⚠️ Erro ao buscar marcadores:", error);
    return [];
  }
}

/**
 * 🦠 BUSCAR SOROLOGIAS
 */
async function buscarSorologias(pacienteId: string): Promise<any[]> {
  try {
    const sorologiasRef = collection(db, "sorologias");
    const q = query(
      sorologiasRef,
      where("pacienteId", "==", pacienteId),
      orderBy("dataColeta", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.warn("⚠️ Erro ao buscar sorologias:", error);
    return [];
  }
}

/**
 * 📷 BUSCAR EXAMES DE IMAGEM
 */
async function buscarExamesImagem(pacienteId: string): Promise<any[]> {
  try {
    const imagensRef = collection(db, "exames-imagem");
    const q = query(
      imagensRef,
      where("pacienteId", "==", pacienteId),
      orderBy("dataExame", "desc")
    );

    const snapshot = await getDocs(q);
    const imagens = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

    // 🔍 DEBUG - Ver estrutura e ordenação dos exames de imagem
    console.log(
      "🔍 Exames de imagem coletados:",
      imagens.map((img: any) => ({
        id: img.id,
        dataExame: img.dataExame || img.data,
        tipo: img.tipo,
      }))
    );
    return imagens;
  } catch (error) {
    console.warn("⚠️ Erro ao buscar exames de imagem:", error);
    return [];
  }
}

/**
 * 💊 BUSCAR PRESCRIÇÕES
 */
async function buscarPrescricoes(pacienteId: string): Promise<any[]> {
  try {
    const prescRef = collection(db, "prescricoes");
    const q = query(
      prescRef,
      where("pacienteId", "==", pacienteId),
      orderBy("criadoEm", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.warn("⚠️ Erro ao buscar prescrições:", error);
    return [];
  }
}

/**
 * 📊 ESTATÍSTICAS DOS DADOS COLETADOS
 * Útil para debug e feedback ao usuário
 */
export async function obterEstatisticasDados(pacienteId: string): Promise<{
  consultas: number;
  examesLab: number;
  marcadores: number;
  sorologias: number;
  imagens: number;
  total: number;
}> {
  const consultas = await buscarConsultas(pacienteId);
  const examesLab = await buscarExamesLaboratoriais(pacienteId);
  const marcadores = await buscarMarcadores(pacienteId);
  const sorologias = await buscarSorologias(pacienteId);
  const imagens = await buscarExamesImagem(pacienteId);

  return {
    consultas: consultas.length,
    examesLab: examesLab.length,
    marcadores: marcadores.length,
    sorologias: sorologias.length,
    imagens: imagens.length,
    total:
      consultas.length +
      examesLab.length +
      marcadores.length +
      sorologias.length +
      imagens.length,
  };
}
